import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmployees, useDeleteEmployee, useEmployeeEditHistory } from '@/hooks/useEmployees';
import { useSalaryPayments, useCreateSalaryPayment } from '@/hooks/useExpenses';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pencil, Trash2, DollarSign, UserCheck, UserX, Plus, Calendar, History } from 'lucide-react';
import { format, startOfMonth, eachMonthOfInterval } from 'date-fns';
import { fr, arSA } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { Employee, AssistantPermissions } from '@/types/entities';
import type { EmployeeWithAudit } from '@/hooks/useEmployees';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { AlertModal } from '@/components/ui/alert-modal';
import { AddEmployeeDialog } from './AddEmployeeDialog';
import { EditEmployeeDialog } from './EditEmployeeDialog';
import { AccountingEditHistoryPanel } from './AccountingEditHistoryPanel';

interface EmployeesListProps {
  restaurantId: string;
  startDate?: string;
  endDate?: string;
}

interface SalaryPayment {
  employeeId: string;
  employeeNameAr: string;
  employeeNameFr: string;
  monthlySalary: number;
  paidAmount: number;
  paymentCount: number;
  lastPaymentDate: string | null;
  isActive: boolean;
  paidMonths: Set<string>; // Set of month keys (YYYY-MM format)
  unpaidMonths: Date[]; // Array of unpaid month dates
}

export function EmployeesList({ restaurantId, startDate, endDate }: EmployeesListProps) {
  const { t, i18n } = useTranslation();
  const { data: assistant } = useCurrentAssistant();
  const {
    data: employees,
    isLoading: employeesLoading,
    refetch: refetchEmployees,
  } = useEmployees(restaurantId, true); // Include deleted items
  const deleteEmployeeMutation = useDeleteEmployee(restaurantId);
  const createSalaryPaymentMutation = useCreateSalaryPayment(restaurantId);

  // Permission helper for assistants
  const hasPermission = (page: keyof AssistantPermissions, action: string): boolean => {
    if (!assistant?.permissions || !assistant.permissions[page]) return false;
    const pagePermissions = assistant.permissions[page] as Record<string, boolean>;
    return pagePermissions[action] === true;
  };

  // Check if user is an assistant
  const isAssistant = !!assistant;

  // Permission checks (admins always have permission)
  const canAdd = !isAssistant || hasPermission('salaries', 'add');
  const canEdit = !isAssistant || hasPermission('salaries', 'edit');
  const canDelete = !isAssistant || hasPermission('salaries', 'delete');

  // Query salary payments - get ALL payments to check paid months
  const {
    data: allSalaryPayments,
    isLoading: salaryPaymentsLoading,
    refetch: refetchSalaryPayments,
  } = useSalaryPayments(restaurantId);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [payingSalaryEmployeeId, setPayingSalaryEmployeeId] = useState<string | null>(null);
  const [deletingEmployeeId, setDeletingEmployeeId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });
  const [historyEmployeeId, setHistoryEmployeeId] = useState<string | null>(null);

  // Get edit history for selected employee
  const { data: editHistory, isLoading: historyLoading } = useEmployeeEditHistory(
    restaurantId,
    historyEmployeeId
  );

  const locale = i18n.language === 'ar' ? arSA : fr;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-MA' : 'fr-MA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Process salary payments
  const salaryPayments: SalaryPayment[] = [];
  if (employees && allSalaryPayments) {
    // Group by employee
    employees.forEach((employee) => {
      // Use ALL payments to check which months are paid
      const allEmployeeExpenses = allSalaryPayments.filter((e) => e.employeeId === employee.id);

      // Use ALL payments for display amounts (not filtered by date range)
      const paidAmount = allEmployeeExpenses.reduce((sum, e) => sum + e.amount, 0);
      const lastPayment = allEmployeeExpenses.sort(
        (a, b) => new Date(b.expenseDate).getTime() - new Date(a.expenseDate).getTime()
      )[0];

      // Track paid months (YYYY-MM format) - use ALL payments
      const paidMonths = new Set<string>();
      allEmployeeExpenses.forEach((expense) => {
        const expenseDate = new Date(expense.expenseDate);
        const monthKey = format(expenseDate, 'yyyy-MM');
        paidMonths.add(monthKey);
      });

      // Find unpaid months from hire date to current month (regardless of date filter)
      const hireDate = new Date(employee.hireDate);
      const hireMonth = startOfMonth(hireDate);
      const now = startOfMonth(new Date()); // Current month

      // Generate all months from hire date to current month
      const allMonthsSinceHire = hireMonth <= now
        ? eachMonthOfInterval({ start: hireMonth, end: now })
        : [];

      // Filter out paid months
      const unpaidMonths = allMonthsSinceHire.filter((month) => {
        const monthKey = format(month, 'yyyy-MM');
        return !paidMonths.has(monthKey);
      });

      salaryPayments.push({
        employeeId: employee.id,
        employeeNameAr: employee.nameAr,
        employeeNameFr: employee.nameFr,
        monthlySalary: employee.monthlySalary,
        paidAmount,
        paymentCount: allEmployeeExpenses.length,
        lastPaymentDate: lastPayment?.expenseDate || null,
        isActive: employee.isActive,
        paidMonths,
        unpaidMonths,
      });
    });
  }

  // Calculate totals
  const totalPaid = salaryPayments.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalExpected = salaryPayments
    .filter((p) => p.isActive)
    .reduce((sum, p) => sum + p.monthlySalary, 0);

  const handleDelete = (employee: Employee) => {
    setConfirmDialog({
      open: true,
      title: t('accounting.deleteEmployee'),
      description: t('accounting.deleteEmployeeConfirm'),
      onConfirm: async () => {
        setDeletingEmployeeId(employee.id);
        try {
          await deleteEmployeeMutation.mutateAsync(employee.id);
          await refetchEmployees();
        } catch (error) {
          console.error('Failed to delete employee:', error);
          setAlertDialog({
            open: true,
            title: t('error.error'),
            description: t('error.generic'),
          });
        } finally {
          setDeletingEmployeeId(null);
        }
      },
    });
  };

  const handlePaySalaryForMonth = async (employee: Employee, monthDate: Date) => {
    setPayingSalaryEmployeeId(employee.id);
    try {
      // Use the first day of the month as the payment date
      const paymentDate = format(startOfMonth(monthDate), 'yyyy-MM-dd');
      await createSalaryPaymentMutation.mutateAsync({
        employeeId: employee.id,
        amount: employee.monthlySalary,
        expenseDate: paymentDate,
        descriptionAr: `${t('accounting.salaryPayment')} - ${employee.nameAr}`,
        descriptionFr: `${t('accounting.salaryPayment')} - ${employee.nameFr}`,
      });
      // Refetch salary payments to update paid months tracking
      await refetchSalaryPayments();
      setAlertDialog({
        open: true,
        title: t('common.success'),
        description: t('accounting.salaryPaid'),
      });
    } catch (error) {
      console.error('Failed to pay salary:', error);
      setAlertDialog({
        open: true,
        title: t('error.error'),
        description: error instanceof Error ? error.message : t('error.generic'),
      });
    } finally {
      setPayingSalaryEmployeeId(null);
    }
  };

  const isLoading = employeesLoading || salaryPaymentsLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <div className="h-10 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {canAdd && (
        <div className="flex justify-end">
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="w-4 h-4 me-2" />
            {t('accounting.addEmployee')}
          </Button>
        </div>
      )}

      {/* Salary Payments Table */}
      {startDate && endDate && (
        <Card>
          <CardHeader>
            <CardTitle>{t('accounting.salaryPayments')}</CardTitle>
          </CardHeader>
          <CardContent>
            {!employees || employees.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {t('accounting.noEmployees')}
              </p>
            ) : (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('accounting.totalExpected')}</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(totalExpected)} {t('common.currency')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('accounting.totalPaid')}</p>
                    <p className="text-xl font-bold text-purple-600">
                      {formatCurrency(totalPaid)} {t('common.currency')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('accounting.paymentCount')}</p>
                    <p className="text-xl font-bold">
                      {salaryPayments.reduce((sum, p) => sum + p.paymentCount, 0)}{' '}
                      {t('accounting.payments')}
                    </p>
                  </div>
                </div>

                {/* Table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('accounting.employee')}</TableHead>
                        <TableHead>{t('accounting.monthlySalary')}</TableHead>
                        <TableHead>{t('accounting.paidAmount')}</TableHead>
                        <TableHead>{t('accounting.payments')}</TableHead>
                        <TableHead>{t('accounting.lastPayment')}</TableHead>
                        <TableHead>{t('accounting.createdBy')}</TableHead>
                        <TableHead className="text-right">{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {salaryPayments.map((payment) => {
                        const employee = employees?.find((e) => e.id === payment.employeeId) as EmployeeWithAudit | undefined;
                        if (!employee) return null;

                        const isDeleted = !!employee.deletedAt;
                        const isPaying = payingSalaryEmployeeId === employee.id;
                        const hasUnpaidMonths = payment.unpaidMonths.length > 0;
                        const currentMonth = new Date();
                        const currentMonthKey = format(currentMonth, 'yyyy-MM');

                        return (
                          <TableRow
                            key={payment.employeeId}
                            className={cn(
                              !payment.isActive && 'opacity-60',
                              isDeleted && 'opacity-60 bg-muted/30'
                            )}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {isDeleted && (
                                  <Badge variant="destructive" className="text-xs">
                                    {t('accounting.deleted')}
                                  </Badge>
                                )}
                                {!isDeleted && payment.isActive && (
                                  <UserCheck className="w-4 h-4 text-green-600" />
                                )}
                                {!isDeleted && !payment.isActive && (
                                  <UserX className="w-4 h-4 text-red-600" />
                                )}
                                <span className={cn('font-medium', isDeleted && 'line-through')}>
                                  {i18n.language === 'ar'
                                    ? payment.employeeNameAr
                                    : payment.employeeNameFr}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="font-mono">
                                {formatCurrency(payment.monthlySalary)} {t('common.currency')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {payment.paidAmount > 0 ? (
                                <span className="font-semibold text-purple-600">
                                  {formatCurrency(payment.paidAmount)} {t('common.currency')}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">
                                  {t('accounting.notPaid')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{payment.paymentCount}</Badge>
                            </TableCell>
                            <TableCell>
                              {payment.lastPaymentDate ? (
                                <span className="text-sm">
                                  {format(new Date(payment.lastPaymentDate), 'dd/MM/yyyy', {
                                    locale,
                                  })}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-sm">
                                  {t('accounting.noPayment')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {employee.createdByDisplay && (
                                  <span className="text-sm text-muted-foreground block">
                                    {employee.createdByDisplay}
                                  </span>
                                )}
                                {isDeleted && employee.updatedByDisplay && (
                                  <span className="text-xs text-muted-foreground italic block">
                                    {t('accounting.deletedBy')}: {employee.updatedByDisplay}
                                  </span>
                                )}
                                {!employee.createdByDisplay && !isDeleted && (
                                  <span className="text-muted-foreground text-sm">-</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                {!isDeleted && canAdd && payment.isActive && hasUnpaidMonths && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="default" size="sm" disabled={isPaying}>
                                        <DollarSign className="w-4 h-4 me-1" />
                                        {t('accounting.paySalary')}
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {payment.unpaidMonths.map((month) => {
                                        const monthKey = format(month, 'yyyy-MM');
                                        const isThisMonth = monthKey === currentMonthKey;
                                        return (
                                          <DropdownMenuItem
                                            key={monthKey}
                                            onClick={() => handlePaySalaryForMonth(employee, month)}
                                            disabled={isPaying}
                                          >
                                            <Calendar className="w-4 h-4 me-2" />
                                            {format(month, 'MMMM yyyy', { locale })}
                                            {isThisMonth && (
                                              <Badge variant="secondary" className="ms-2 text-xs">
                                                {t('accounting.currentMonth')}
                                              </Badge>
                                            )}
                                          </DropdownMenuItem>
                                        );
                                      })}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                                {!isDeleted && payment.isActive && !hasUnpaidMonths && (
                                  <Badge variant="outline" className="text-green-600">
                                    {t('accounting.allPaid')}
                                  </Badge>
                                )}
                                {!isDeleted && canEdit && (
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => setEditingEmployee(employee)}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                )}
                                {!isDeleted && canDelete && (
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleDelete(employee)}
                                    disabled={deletingEmployeeId === employee.id}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setHistoryEmployeeId(employee.id)}
                                  title={t('accounting.editHistory.title')}
                                >
                                  <History className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Employee Cards (fallback if no date range) */}
      {(!startDate || !endDate) && (
        <>
          {!employees || employees.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">{t('accounting.noEmployees')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {employees.map((employee) => {
                const employeeWithAudit = employee as EmployeeWithAudit;
                const isDeleted = !!employeeWithAudit.deletedAt;

                return (
                  <Card
                    key={employee.id}
                    className={cn(
                      !employee.isActive && 'opacity-60',
                      isDeleted && 'opacity-60 bg-muted/30'
                    )}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {isDeleted && (
                              <Badge variant="destructive" className="text-xs">
                                {t('accounting.deleted')}
                              </Badge>
                            )}
                            <h3 className={cn('font-semibold truncate', isDeleted && 'line-through')}>
                              {i18n.language === 'ar' ? employee.nameAr : employee.nameFr}
                            </h3>
                            {!isDeleted && employee.isActive && (
                              <UserCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                            )}
                            {!isDeleted && !employee.isActive && (
                              <UserX className="w-4 h-4 text-red-600 flex-shrink-0" />
                            )}
                          </div>
                          {(employee.positionAr || employee.positionFr) && (
                            <p className={cn('text-sm text-muted-foreground truncate', isDeleted && 'line-through')}>
                              {i18n.language === 'ar' ? employee.positionAr : employee.positionFr}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t('accounting.monthlySalary')}
                          </span>
                          <Badge
                            variant="secondary"
                            className={cn('font-mono', isDeleted && 'line-through')}
                          >
                            {formatCurrency(employee.monthlySalary)} {t('common.currency')}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t('accounting.hireDate')}
                          </span>
                          <span className={cn('text-sm', isDeleted && 'line-through')}>
                            {format(new Date(employee.hireDate), 'dd/MM/yyyy', { locale })}
                          </span>
                        </div>
                        {employeeWithAudit.createdByDisplay && (
                          <p className="text-xs text-muted-foreground italic">
                            {t('accounting.createdBy')}: {employeeWithAudit.createdByDisplay}
                          </p>
                        )}
                        {isDeleted && employeeWithAudit.updatedByDisplay && (
                          <p className="text-xs text-muted-foreground italic">
                            {t('accounting.deletedBy')}: {employeeWithAudit.updatedByDisplay}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2 pt-3 border-t">
                        {!isDeleted && canEdit && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setEditingEmployee(employee)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {!isDeleted && canDelete && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleDelete(employee)}
                            disabled={deletingEmployeeId === employee.id}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setHistoryEmployeeId(employee.id)}
                          title={t('accounting.editHistory.title')}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <AddEmployeeDialog
        restaurantId={restaurantId}
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={() => refetchEmployees()}
      />

      {editingEmployee && (
        <EditEmployeeDialog
          restaurantId={restaurantId}
          employee={editingEmployee}
          open={!!editingEmployee}
          onOpenChange={(open) => !open && setEditingEmployee(null)}
          onSuccess={() => refetchEmployees()}
        />
      )}

      <ConfirmModal
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant="destructive"
      />

      <AlertModal
        open={alertDialog.open}
        onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
        title={alertDialog.title}
        description={alertDialog.description}
      />

      {/* Edit History Dialog */}
      <Dialog open={!!historyEmployeeId} onOpenChange={(open) => !open && setHistoryEmployeeId(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('accounting.editHistory.title')}</DialogTitle>
          </DialogHeader>
          <AccountingEditHistoryPanel
            entries={editHistory || []}
            isLoading={historyLoading}
            type="employee"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
